// How long things can be muted for, and how a mute's end reads. Chats, groups, channels and spaces
// all offer the same choices with the same words.

/** Muted until turned back on. The server keeps it as a date far in the future. */
export const ALWAYS = "9999-12-31T23:59:59Z";

export type MuteChoice = { id: "1h" | "8h" | "1w" | "always"; label: string; ms: number | null };

export const MUTE_CHOICES: MuteChoice[] = [
  { id: "1h", label: "For 1 hour", ms: 3_600_000 },
  { id: "8h", label: "For 8 hours", ms: 8 * 3_600_000 },
  { id: "1w", label: "For 1 week", ms: 7 * 86_400_000 },
  { id: "always", label: "Until I turn it back on", ms: null },
];

/** When a mute chosen now ends, as the server takes it. */
export function muteEnd(choice: MuteChoice, now = Date.now()): string {
  return choice.ms === null ? ALWAYS : new Date(now + choice.ms).toISOString();
}

/** Whether a mute is in force. */
export function isMutedUntil(until: string | null | undefined, now = Date.now()): boolean {
  return Boolean(until && new Date(until).getTime() > now);
}

/** Whether a mute lasts until it is turned back on. */
export function mutedForever(until: string | null | undefined): boolean {
  return Boolean(until && new Date(until).getFullYear() > 3000);
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * The end of a mute in a few words, such as "14:30", "Tue 09:00" or "10 Nov". Empty when it is
 * not muted or is muted until turned back on.
 */
export function muteEndLabel(until: string | null | undefined, now = new Date()): string {
  if (!isMutedUntil(until, now.getTime()) || mutedForever(until)) return "";
  const d = new Date(until as string);
  const time = `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (sameDay(d, now)) return time;
  if (d.getTime() - now.getTime() < 6 * 86_400_000)
    return `${d.toLocaleDateString("en-GB", { weekday: "short" })} ${time}`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** The note after muting, such as "Muted until 14:30." */
export function mutedNote(until: string): string {
  const end = muteEndLabel(until);
  return end ? `Muted until ${end}.` : "Muted until you turn it back on.";
}
